import { snakeCase } from 'lodash';
import React, { ComponentProps, FC, ReactNode } from 'react';
import { ActionFooter } from 'components/molecules/action-footer';
import { ActionPanel } from 'components/molecules/action-panel';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { CreatableEditableRefreshableContent } from 'stores/core';
import { Task } from 'stores/tasks';
import { DateField } from '../../fields';
import { OnUpdate } from '../bulk-editor';
import { Form, Popover, Select } from './components';
import { DirectionOptions, ShiftByOptions } from './model';
import { useShiftDatesState } from './use-shift-dates-state';

const {
  PopoverAction,
  Popover: { Title, Divider },
} = ActionPanel;

interface Props {
  startDate: DateField;
  dueDate: DateField;
  ids: string[];
  getLookup: () => Record<number, CreatableEditableRefreshableContent<Task>>;
  title: ReactNode;
  allowed: ComponentProps<typeof PopoverAction>['allowed'];
  onUpdate: OnUpdate;
}

export const ShiftDates: FC<Props> = ({ title, allowed, ...rest }) => {
  const translate = useTranslate();
  const {
    innerRef,
    clear,
    count,
    onCountChange,
    options,
    shiftBy,
    onShiftByChange,
    direction,
    onDirectionChange,
    primary,
    secondary,
    other,
  } = useShiftDatesState(rest);

  return (
    <PopoverAction
      ref={innerRef}
      title={title}
      allowed={allowed}
      onShow={clear}
    >
      <Popover>
        <Title>{title}</Title>
        <Form layout="inline">
          <Form.Item>
            <Select
              data-cy="shift.dates.duration"
              virtual={false}
              value={count}
              onChange={onCountChange}
              $width={48}
            >
              {options.map((option) => (
                <Select.Option
                  key={option}
                  value={option}
                  title={option.toString()}
                >
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Select
              data-cy="shift.dates.unit"
              virtual={false}
              value={shiftBy}
              onChange={onShiftByChange}
              $width={72}
            >
              {ShiftByOptions.map((option) => {
                const translation = translate(
                  `task_list.bulk_editor.options.shift_dates.shift_by.${snakeCase(
                    option,
                  )}` as TranslationKey,
                );

                return (
                  <Select.Option
                    key={option}
                    value={option}
                    title={translation}
                  >
                    {translation}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item>
            <Select
              data-cy="shift.dates.later_earlier"
              virtual={false}
              value={direction}
              onChange={onDirectionChange}
              $width={64}
            >
              {DirectionOptions.map((option) => {
                const translation = translate(
                  `task_list.bulk_editor.options.shift_dates.direction.${snakeCase(
                    option,
                  )}` as TranslationKey,
                );

                return (
                  <Select.Option
                    key={option}
                    value={option}
                    title={translation}
                  >
                    {translation}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Form>
        <Divider />
        <ActionFooter primary={primary} secondary={secondary} other={other} />
      </Popover>
    </PopoverAction>
  );
};
